import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, IconButton, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { CashbookAccessService } from '../../services/cashbookAccess.service';
import { Cashbook, CashbookAccessUser } from '../../interfaces/cashbook/cashbook.interface';
import { SearchableSelect } from '../common/SearchableSelect';
import EmployeeAutocomplete, { EmployeeOption } from '../common/EmployeeAutocomplete';

/** Polish P2-1: picker and table name a user the same way, full name plus the full (prefixed) username. */
const userLabel = (u: { username: string; fullName?: string }): string =>
  u.fullName?.trim() ? `${u.fullName.trim()} (${u.username})` : u.username;

const cashbookLabel = (c: Cashbook) =>
  `${c.code} - ${c.name}${c.bankName ? ` (${c.bankName})` : ''}${c.restricted ? ' · restricted' : ''}`;

/** Banking > Cashbook access (F11 11b): limit who may see and post to each cash or bank account. An account with no
 *  listed user is open to everyone with the usual permissions; once a user is listed, only listed users (and
 *  administrators) see its transactions, statements and balances, and only those with "Post" may record to it. */
const CashbookAccess: React.FC = () => {
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution()!;
  const [cashbooks, setCashbooks] = useState<Cashbook[]>([]);
  const [accountId, setAccountId] = useState('');
  const [users, setUsers] = useState<CashbookAccessUser[]>([]);
  const [picker, setPicker] = useState<EmployeeOption | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const loadCashbooks = useCallback(async () => {
    try {
      const list = await CashbookAccessService.cashbooks(institutionId);
      setCashbooks(Array.isArray(list) ? list : []);
    } catch (e) {
      toast.showApiError(e, 'Failed to load cash and bank accounts');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [institutionId]);

  useEffect(() => { loadCashbooks(); }, [loadCashbooks]);

  useEffect(() => {
    if (!accountId) { setUsers([]); return; }
    let live = true;
    setLoading(true);
    Promise.resolve(CashbookAccessService.get(institutionId, accountId))
      .then(res => { if (live) { setUsers(res?.users ?? []); setDirty(false); } })
      .catch((e: unknown) => toast.showApiError(e, 'Failed to load access for the account'))
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [institutionId, accountId]);

  const addUser = (u: EmployeeOption | null) => {
    setPicker(null);
    if (!u || users.some(x => x.userId === u.id)) return;
    setUsers(rows => [...rows, { userId: u.id, username: u.username, fullName: u.fullName ?? null, canView: true, canPost: false }]);
    setDirty(true);
  };
  const patch = (userId: string, change: Partial<CashbookAccessUser>) => {
    setUsers(rows => rows.map(r => {
      if (r.userId !== userId) return r;
      const next = { ...r, ...change };
      if (change.canPost) next.canView = true;          // post implies view
      if (change.canView === false) next.canPost = false;
      return next;
    }));
    setDirty(true);
  };
  const drop = (userId: string) => { setUsers(rows => rows.filter(r => r.userId !== userId)); setDirty(true); };

  const save = async () => {
    try {
      setSaving(true);
      const res = await CashbookAccessService.replace(institutionId, accountId,
        users.map(u => ({ userId: u.userId, canView: u.canView, canPost: u.canPost })));
      setUsers(res?.users ?? []);
      setDirty(false);
      toast.showSuccess(users.length ? 'Access saved' : 'Restrictions removed');
      await loadCashbooks();
    } catch (e) {
      toast.showApiError(e, 'Failed to save access');
    } finally {
      setSaving(false);
    }
  };
  const clearAll = async () => {
    if (!window.confirm('Remove every restriction on this account? Everyone with the usual permissions will see and use it again.')) return;
    try {
      setSaving(true);
      await CashbookAccessService.clear(institutionId, accountId);
      setUsers([]);
      setDirty(false);
      toast.showSuccess('Restrictions removed');
      await loadCashbooks();
    } catch (e) {
      toast.showApiError(e, 'Failed to remove the restrictions');
    } finally {
      setSaving(false);
    }
  };

  const current = cashbooks.find(c => c.id === accountId);

  return (
    <Box data-help-page="banking.cashbookAccess.page" sx={{ p: 3 }}>
      <Typography variant="h5">Cashbook access</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        An account with no listed user is open to everyone with the usual permissions. Once you list users, only they
        (and administrators) find it in the cash form, the transaction list, its ledger and the cash transactions import;
        only users with Post may record to it.
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box data-help="banking.cashbookAccess.account" sx={{ flex: '1 1 320px', maxWidth: 480 }}>
          <SearchableSelect<Cashbook>
            options={cashbooks} value={accountId} onChange={id => setAccountId(id)} size="small"
            getOptionLabel={cashbookLabel} label="Cash or bank account" placeholder="Choose an account"
            data-testid="cashbook-select" />
        </Box>
      </Box>

      {accountId && (loading ? <CircularProgress /> : (
        <Paper data-help-section="banking.cashbookAccess.users" variant="outlined" sx={{ p: 2 }}>
          <Alert severity={users.length ? 'warning' : 'info'} sx={{ mb: 2 }} data-testid="cashbook-state">
            {users.length
              ? `${current?.name ?? 'This account'} is restricted to the users below.`
              : `${current?.name ?? 'This account'} is open: add a user to restrict it.`}
          </Alert>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <Box data-help="banking.cashbookAccess.addUser" sx={{ flex: '1 1 280px', maxWidth: 420 }}>
              <EmployeeAutocomplete value={picker} onChange={addUser} label="Add a user" size="small" activeOnly
                getOptionLabel={userLabel}
                renderOption={(props, option) => {
                  const { key: _key, ...liProps } = props as typeof props & { key?: React.Key };
                  return <li {...liProps} key={option.id}>{userLabel(option)}</li>;
                }}
                data-testid="cashbook-add-user" />
            </Box>
          </Box>
          <TableContainer data-help-section="banking.cashbookAccess.table">
            <Table size="small" data-testid="cashbook-users">
              <TableHead>
                <TableRow>
                  <TableCell data-help="banking.cashbookAccess.col.user">User</TableCell>
                  <TableCell data-help="banking.cashbookAccess.col.view" align="center">View</TableCell>
                  <TableCell data-help="banking.cashbookAccess.col.post" align="center">Post</TableCell>
                  <TableCell data-help="banking.cashbookAccess.col.actions" />
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map(u => (
                  <TableRow key={u.userId}>
                    <TableCell>{u.username ? userLabel({ username: u.username, fullName: u.fullName ?? undefined }) : u.userId}</TableCell>
                    <TableCell align="center">
                      <Checkbox data-help="banking.cashbookAccess.action.view" size="small" checked={u.canView} onChange={e => patch(u.userId, { canView: e.target.checked })}
                        inputProps={{ 'aria-label': `View ${u.username ?? u.userId}` }} />
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox data-help="banking.cashbookAccess.action.post" size="small" checked={u.canPost} onChange={e => patch(u.userId, { canPost: e.target.checked })}
                        inputProps={{ 'aria-label': `Post ${u.username ?? u.userId}` }} />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton data-help="banking.cashbookAccess.action.remove" size="small" color="error" onClick={() => drop(u.userId)} aria-label={`Remove ${u.username ?? u.userId}`}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {users.length === 0 && (
                  <TableRow><TableCell colSpan={4} align="center">No users listed.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }} data-testid="cashbook-view-note">
            View hides the cash form, transaction list and ledger. Users with journal-entry or report access still see this
            account's movements there.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
            <Button data-help="banking.cashbookAccess.clearAll" color="error" disabled={saving || !current?.restricted} onClick={clearAll}>Remove all restrictions</Button>
            <Button data-help="banking.cashbookAccess.save" variant="contained" disabled={saving || !dirty} onClick={save}>Save</Button>
          </Box>
        </Paper>
      ))}
    </Box>
  );
};

export default CashbookAccess;
